import { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Globe, ExternalLink, Mail, X, MapPin as MapPinIcon } from 'lucide-react';
import { distributorsData } from '../data/distributors';
import 'leaflet/dist/leaflet.css';

// Custom orange marker icon
const createCustomIcon = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="position: relative; width: 32px; height: 32px;">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="#FF5500" stroke="#ffffff" stroke-width="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
          <circle cx="12" cy="10" r="3" fill="#ffffff"></circle>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

const EuropeDistributorsMap = () => {
  const [selectedCountry, setSelectedCountry] = useState(null);

  // GPS coordinates for each country (approximate capital/center)
  const countryCoordinates = {
    portugal: [39.3999, -8.2245],
    spain: [40.4637, -3.7492],
    uk: [54.5973, -3.9980],
    france: [46.2276, 2.2137],
    belgium: [50.5039, 4.4699],
    netherlands: [52.1326, 5.2913],
    germany: [51.1657, 10.4515],
    switzerland: [46.8182, 8.2275],
    austria: [47.5162, 14.5501],
    denmark: [56.2639, 9.5018],
    sweden: [60.1282, 18.6435],
    finland: [61.9241, 25.7482],
    poland: [51.9194, 19.1451],
    lithuania: [55.1694, 23.8813],
    slovakia: [48.6690, 19.6990],
    hungary: [47.1625, 19.5033],
    croatia: [45.1000, 15.2000],
    slovenia: [46.1512, 14.9955],
    bulgaria: [42.7339, 25.4858],
    greece: [39.0742, 21.8243],
    ukraine: [48.3794, 31.1656],
    luxembourg: [49.8153, 6.1296],
  };

  const handleMarkerClick = (countryId) => {
    setSelectedCountry(countryId);
  };

  return (
    <section className="py-20 bg-[#050505]" id="distribuce">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Globe className="w-10 h-10 text-[#FF5500]" />
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              DISTRIBUTOŘI V EVROPĚ
            </h2>
          </div>
          <p className="text-xl text-zinc-400 max-w-3xl mx-auto">
            Interaktivní mapa našich distributorů - klikněte na značku pro detail
          </p>
        </div>

        {/* Leaflet Map */}
        <div className="bg-[#0a0a0a] border border-zinc-800 rounded-sm overflow-hidden shadow-lg mb-12">
          <div className="h-[600px] relative">
            <MapContainer
              center={[50.0, 15.0]}
              zoom={4}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* Markers for each country */}
              {Object.entries(countryCoordinates).map(([countryId, coords]) => {
                const countryData = distributorsData[countryId];
                if (!countryData) return null;

                return (
                  <Marker
                    key={countryId}
                    position={coords}
                    icon={createCustomIcon()}
                    eventHandlers={{
                      click: () => handleMarkerClick(countryId)
                    }}
                  >
                    <Popup>
                      <div className="p-2 min-w-[200px]">
                        <h3 className="font-bold text-lg text-[#1a1a1a] mb-2">
                          {countryData.name}
                        </h3>
                        <p className="text-sm text-zinc-600 mb-3">
                          {countryData.distributors.length} distributor{countryData.distributors.length > 1 ? 'ů' : ''}
                        </p>
                        <button
                          onClick={() => setSelectedCountry(countryId)}
                          className="w-full bg-[#FF5500] hover:bg-[#ff6622] text-white px-3 py-2 rounded-sm text-sm transition-colors"
                        >
                          Zobrazit distributory
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
          
          <div className="p-4 bg-[#121212] border-t border-zinc-800 text-center">
            <p className="text-zinc-400 text-sm">
              💡 <strong className="text-white">Tip:</strong> Použijte kolečko myši pro přiblížení/oddálení • Tažením pohybujte mapou
            </p>
          </div>
        </div>

        {/* Selected Country Modal */}
        {selectedCountry && distributorsData[selectedCountry] && (
          <div 
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4"
            onClick={() => setSelectedCountry(null)}
          >
            <div 
              className="bg-white rounded-sm max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-zinc-200 p-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MapPinIcon className="w-6 h-6 text-[#FF5500]" />
                  <h3 className="text-2xl font-bold text-[#1a1a1a]">
                    {distributorsData[selectedCountry].name}
                  </h3>
                </div>
                <button
                  onClick={() => setSelectedCountry(null)}
                  className="text-zinc-400 hover:text-[#1a1a1a] transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                {distributorsData[selectedCountry].distributors.map((dist, idx) => (
                  <div
                    key={idx}
                    className="bg-zinc-50 border border-zinc-200 rounded-sm p-4"
                  >
                    <p className="font-semibold text-[#1a1a1a] text-lg mb-2">{dist.name}</p>
                    {dist.address && (
                      <p className="text-zinc-600 text-sm mb-2">{dist.address}</p>
                    )}
                    <div className="flex flex-wrap gap-3 mt-3">
                      {dist.website && (
                        <a
                          href={dist.website.startsWith('http') ? dist.website : `https://${dist.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FF5500] hover:bg-[#ff6622] text-white text-sm rounded-sm transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Navštívit web
                        </a>
                      )}
                      {dist.email && (
                        <a
                          href={`mailto:${dist.email}`}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-200 hover:bg-zinc-300 text-[#1a1a1a] text-sm rounded-sm transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          {dist.email}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#0a0a0a] border border-zinc-800 rounded-sm p-6 text-center">
            <p className="text-4xl font-bold text-[#FF5500]">
              {Object.keys(distributorsData).length}
            </p>
            <p className="text-zinc-400 text-sm mt-2">Zemí</p>
          </div>
          <div className="bg-[#0a0a0a] border border-zinc-800 rounded-sm p-6 text-center">
            <p className="text-4xl font-bold text-[#FF5500]">
              {Object.values(distributorsData).reduce((sum, country) => sum + country.distributors.length, 0)}
            </p>
            <p className="text-zinc-400 text-sm mt-2">Distributorů</p>
          </div>
          <div className="bg-[#0a0a0a] border border-zinc-800 rounded-sm p-6 text-center">
            <p className="text-4xl font-bold text-[#FF5500]">15+</p>
            <p className="text-zinc-400 text-sm mt-2">Let zkušeností</p>
          </div>
          <div className="bg-[#0a0a0a] border border-zinc-800 rounded-sm p-6 text-center">
            <p className="text-4xl font-bold text-[#FF5500]">100%</p>
            <p className="text-zinc-400 text-sm mt-2">Kvalita</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EuropeDistributorsMap;